'use client';

import { usePreference } from '@/hooks/use-preference';
import type { NotificationType } from '@/lib/notification-type';
import { readPreference, writePreference } from '@/lib/preferences';
import { trpc, useOptionalTrpcUtils } from '@/trpc/client';
import { Cookie } from '@/types/types';
import { assignLocation } from '@/utils/assign-location';
import { notifyForegroundMessage } from '@/utils/foreground-notifications';
import { refreshAndOptimisticallyUpdateChat } from '@/utils/push-query-refresher';
import { reloadPage } from '@/utils/reload-page';
import { isNativeAppWebView } from '@/utils/standalone-check';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
export type NativePushStatus = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * How long to wait after the initial getStatus() before concluding that the
 * native bridge is not delivering events back to the page.
 */
const BRIDGE_RECOVERY_TIMEOUT_MS = 5000;
/** Minimum time between recovery reload attempts (guards against reload loops). */
const BRIDGE_RECOVERY_RETRY_INTERVAL_MS = 10 * 60 * 1000;
const BRIDGE_RECOVERY_ATTEMPT_STORAGE_KEY = 'native_push_bridge_recovery_at';
declare global {
  var AppWebViewNativePush: {
    getStatus: () => void;
    requestPermission: () => void;
    deleteToken: () => void;
    openSettings: () => void;
    /**
     * Renders a system notification while the app is in the foreground.
     * Only available on native app builds that implement the
     * `native-push-show-notification` bridge command; feature-detect before calling.
     */
    showNotification?: (request: Record<string, string>) => void;
  } | undefined;
}

/**
 * Interface wrapper for AppWebViewNativePush global object to encapsulate bridge calls.
 */
const nativePushBridge = {
  isSupported: (): boolean => typeof globalThis !== 'undefined' && globalThis.AppWebViewNativePush !== undefined,
  getStatus: (): void => {
    const bridge = globalThis.AppWebViewNativePush as Record<string, unknown> | undefined;
    if (!bridge) return;
    switch (true) {
      case typeof bridge['getStatus'] === 'function':
        {
          (bridge['getStatus'] as () => void)();
          break;
        }
      case typeof bridge['checkStatus'] === 'function':
        {
          (bridge['checkStatus'] as () => void)();
          break;
        }
      case typeof bridge['getStatusAsync'] === 'function':
        {
          void (bridge['getStatusAsync'] as () => Promise<void>)();
          break;
        }
    }
  },
  requestPermission: (): void => {
    const bridge = globalThis.AppWebViewNativePush as Record<string, unknown> | undefined;
    if (!bridge) return;
    if (typeof bridge['requestPermission'] === 'function') (bridge['requestPermission'] as () => void)();
    if (typeof bridge['requestPermissions'] === 'function') (bridge['requestPermissions'] as () => void)();
    if (typeof bridge['requestPushPermission'] === 'function') (bridge['requestPushPermission'] as () => void)();
    if (typeof bridge['requestNotificationPermission'] === 'function') (bridge['requestNotificationPermission'] as () => void)();
    if (typeof bridge['promptPermission'] === 'function') (bridge['promptPermission'] as () => void)();
  },
  deleteToken: (): void => {
    const bridge = globalThis.AppWebViewNativePush as Record<string, unknown> | undefined;
    if (!bridge) return;
    switch (true) {
      case typeof bridge['deleteToken'] === 'function':
        {
          (bridge['deleteToken'] as () => void)();
          break;
        }
      case typeof bridge['unregister'] === 'function':
        {
          (bridge['unregister'] as () => void)();
          break;
        }
      case typeof bridge['removeToken'] === 'function':
        {
          (bridge['removeToken'] as () => void)();
          break;
        }
    }
  },
  openSettings: (): void => {
    const bridge = globalThis.AppWebViewNativePush as Record<string, unknown> | undefined;
    if (!bridge) return;
    switch (true) {
      case typeof bridge['openSettings'] === 'function':
        {
          (bridge['openSettings'] as () => void)();
          break;
        }
      case typeof bridge['openNotificationSettings'] === 'function':
        {
          (bridge['openNotificationSettings'] as () => void)();
          break;
        }
    }
  }
};
interface NativePushEventDetail {
  type?: string;
  payload?: Record<string, unknown>;
}
export function extractTargetUrl(payload: Record<string, unknown>): string | undefined {
  const notificationObject = payload['notification'] as Record<string, unknown> | undefined;
  const apsObject = (payload['aps'] ?? notificationObject?.['aps']) as Record<string, unknown> | undefined;
  const alertObject = (apsObject?.['alert'] ?? notificationObject?.['alert']) as Record<string, unknown> | undefined;
  const userInfoObject = (payload['userInfo'] ?? notificationObject?.['userInfo']) as Record<string, unknown> | undefined;
  const candidates: (Record<string, unknown> | undefined)[] = [payload, payload['data'] as Record<string, unknown> | undefined, notificationObject, notificationObject?.['data'] as Record<string, unknown> | undefined, apsObject, alertObject, userInfoObject];

  // First pass: Check for explicit chatId across all candidates
  for (const object_ of candidates) {
    if (!object_ || typeof object_ !== 'object') continue;
    const chatId: unknown = object_['chatId'];
    if (typeof chatId === 'string' && chatId.trim() !== '') {
      return `/app/chat/${chatId.trim()}`;
    }
    if (typeof chatId === 'number') {
      return `/app/chat/${String(chatId)}`;
    }
  }

  // Second pass: Check for explicit URL / path properties
  for (const object_ of candidates) {
    if (!object_ || typeof object_ !== 'object') continue;
    for (const key of ['redirectTo', 'url', 'path', 'link', 'target']) {
      const val: unknown = object_[key];
      if (typeof val === 'string' && val.trim() !== '') {
        return val.trim();
      }
    }
  }
  return undefined;
}

/**
 * Reads a navigation target as a browser would: a path when it is on this origin, the full URL
 * when it is on another one, nothing when it is not http(s).
 *
 * Every value is parsed, also one that starts with a slash: a browser reads `/\host` and
 * `//host` as another origin, so the leading slash alone says nothing.
 */
function parseHttpTarget(rawUrl: string): string | undefined {
  try {
    const parsedUrl = new URL(rawUrl, globalThis.location.origin);
    if (parsedUrl.protocol !== 'https:' && parsedUrl.protocol !== 'http:') return undefined;
    if (parsedUrl.origin !== globalThis.location.origin) return parsedUrl.href;
    // `https://this-host//other-host` is on this origin, but its path alone names another one,
    // and the path is all that is handed on.
    if (parsedUrl.pathname.startsWith('//')) return undefined;
    return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
  } catch {
    return undefined;
  }
}

/**
 * Whether the page is at a same-origin target. Query and fragment count, because they select
 * what the page shows.
 */
const isAtTarget = (target: string): boolean => {
  const {
    pathname,
    search,
    hash
  } = globalThis.location;
  return pathname + search + hash === target;
};

/**
 * Turns the URL a push carries into where the page goes: a path when the target is on this
 * origin, the full URL when it is on another one. The short domain con27.ch is another origin
 * that redirects back here through `/go`, and cutting it down to its path sent `/agbs` to a
 * 404. Anything that is not http(s) falls back to the dashboard.
 */
export function resolvePushTarget(rawUrl: string | undefined): string {
  const fallback = '/app/dashboard';
  if (rawUrl === undefined || rawUrl === '') return fallback;
  return parseHttpTarget(rawUrl) ?? fallback;
}

/** Whether a target from {@link resolvePushTarget} is on another origin. */
const isOtherOriginTarget = (target: string): boolean => !target.startsWith('/');

/**
 * The path a link asks for, when that path is on this origin. A query parameter is written by
 * whoever built the link, so unlike a push it may not send the page to another origin.
 */
export function resolveSameOriginPath(rawUrl: string | null | undefined): string | undefined {
  if (rawUrl === null || rawUrl === undefined || rawUrl.trim() === '') return undefined;
  const target = parseHttpTarget(rawUrl.trim());
  return target === undefined || isOtherOriginTarget(target) ? undefined : target;
}
export function performReliablePushNavigation(router: {
  push: (url: string) => void;
}, targetPath: string): void {
  if (typeof targetPath !== 'string' || targetPath.trim() === '') return;

  // Resolved here, in front of the navigation itself, so that no caller can hand it a
  // `javascript:` URL: `location.assign()` would run it in this origin.
  const cleanPath = resolvePushTarget(targetPath.trim());

  // The router only moves within this origin, and a remembered target would be replayed on
  // every resume, so another origin gets a single full navigation.
  if (isOtherOriginTarget(cleanPath)) {
    console.log('[NativePush:PWA] Navigating to another origin:', cleanPath);
    assignLocation(cleanPath);
    return;
  }
  try {
    sessionStorage.setItem('pending_push_redirect', cleanPath);
    localStorage.setItem('pending_push_redirect', cleanPath);
  } catch {
    // ignore storage errors
  }
  console.log('[NativePush:PWA] Performing reliable navigation to:', cleanPath);
  try {
    router.push(cleanPath);
  } catch (error: unknown) {
    console.warn('[NativePush:PWA] Soft router.push failed:', error);
  }

  // Backup check: perform hard location redirect if router hasn't updated pathname after 250ms
  setTimeout(() => {
    const currentPathname = globalThis.location.pathname;
    let targetChatId: string | undefined;
    if (cleanPath.includes('/app/chat/')) {
      const parts = cleanPath.split('/app/chat/');
      const firstPart = parts[1];
      if (typeof firstPart === 'string' && firstPart !== '') {
        targetChatId = firstPart.split(/[?#]/)[0];
      }
    }
    const isAlreadyOnChat = typeof targetChatId === 'string' && targetChatId !== '' && currentPathname.includes(`/app/chat/${targetChatId}`);
    const isAlreadyOnExactPath = isAtTarget(cleanPath);
    if (!isAlreadyOnChat && !isAlreadyOnExactPath) {
      console.log('[NativePush:PWA] Soft navigation pending, executing location fallback to:', cleanPath);
      globalThis.location.href = cleanPath;
    }
  }, 250);
}
export function checkAndExecutePendingPushNavigation(router: {
  push: (url: string) => void;
}): void {
  try {
    const pendingPath = sessionStorage.getItem('pending_push_redirect') ?? localStorage.getItem('pending_push_redirect');
    if (typeof pendingPath !== 'string' || pendingPath.trim() === '') return;
    const cleanPath = resolvePushTarget(pendingPath.trim());
    const currentPathname = globalThis.location.pathname;
    let targetChatId: string | undefined;
    if (cleanPath.includes('/app/chat/')) {
      const parts = cleanPath.split('/app/chat/');
      const firstPart = parts[1];
      if (typeof firstPart === 'string' && firstPart !== '') {
        targetChatId = firstPart.split(/[?#]/)[0];
      }
    }
    const isAlreadyOnChat = typeof targetChatId === 'string' && targetChatId !== '' && currentPathname.includes(`/app/chat/${targetChatId}`);
    const isAlreadyOnExactPath = isAtTarget(cleanPath);
    if (isAlreadyOnChat || isAlreadyOnExactPath) {
      sessionStorage.removeItem('pending_push_redirect');
      localStorage.removeItem('pending_push_redirect');
      return;
    }
    console.log('[NativePush:PWA] Executing pending push navigation to:', cleanPath);
    try {
      router.push(cleanPath);
    } catch {
      // ignore soft error
    }
    setTimeout(() => {
      const nowPathname = globalThis.location.pathname;
      const nowOnChat = typeof targetChatId === 'string' && targetChatId !== '' && nowPathname.includes(`/app/chat/${targetChatId}`);
      const nowOnExact = isAtTarget(cleanPath);
      if (!nowOnChat && !nowOnExact) {
        console.log('[NativePush:PWA] Fallback location redirect to:', cleanPath);
        globalThis.location.href = cleanPath;
      } else {
        sessionStorage.removeItem('pending_push_redirect');
        localStorage.removeItem('pending_push_redirect');
      }
    }, 250);
  } catch {
    // ignore storage errors
  }
}
export function extractNotificationTitleAndBody(payload: Record<string, unknown>): {
  title: string;
  body: string;
} {
  const notificationObject = payload['notification'] as Record<string, unknown> | undefined;
  const dataObject = payload['data'] as Record<string, unknown> | undefined;
  const apsObject = (payload['aps'] ?? notificationObject?.['aps']) as Record<string, unknown> | undefined;
  const alertObject = (apsObject?.['alert'] ?? notificationObject?.['alert']) as Record<string, unknown> | undefined;
  const userInfoObject = (payload['userInfo'] ?? notificationObject?.['userInfo']) as Record<string, unknown> | undefined;
  const candidates: (Record<string, unknown> | undefined)[] = [payload, dataObject, notificationObject, notificationObject?.['data'] as Record<string, unknown> | undefined, apsObject, alertObject, userInfoObject];
  let title = 'Neue Nachricht';
  let body = '';
  for (const object_ of candidates) {
    if (!object_ || typeof object_ !== 'object') continue;
    const titleValue = object_['title'];
    if (typeof titleValue === 'string' && titleValue.trim() !== '') {
      title = titleValue.trim();
      break;
    }
  }
  for (const object_ of candidates) {
    if (!object_ || typeof object_ !== 'object') continue;
    for (const key of ['body', 'message', 'content', 'text']) {
      const value = object_[key];
      if (typeof value === 'string' && value.trim() !== '') {
        body = value.trim();
        break;
      }
    }
    if (body !== '') break;
  }
  return {
    title,
    body
  };
}

/**
 * Extracts the id identifying the underlying chat message so that the same
 * message delivered over FCM and over SSE is only surfaced once.
 *
 * The `data` blocks are searched before the top level on purpose: the native
 * shell normalises a Firebase message to `{ messageId, ..., data }`, where the
 * top-level `messageId` is Firebase's own per-delivery id and only
 * `data.messageId` is the chat message id that SSE also reports. Reading the
 * top level first would produce a key SSE can never match, and the message would
 * be surfaced twice.
 *
 * Falls back to the push log id, which is at least unique per notification.
 */
export function extractMessageIdentifier(payload: Record<string, unknown>): string | undefined {
  return findPayloadString(payload, ['messageId', 'notificationId']);
}

/**
 * Extracts the push log id the server put into the FCM `data` payload, which is what
 * delivery and interaction tracking key on.
 */
export function extractNotificationLogId(payload: Record<string, unknown>): string | undefined {
  return findPayloadString(payload, ['notificationId']);
}

/**
 * Returns the first non-empty string found under one of `keys`, trying each key across
 * all the places the native shell may have put the FCM data before moving to the next.
 */
function findPayloadString(payload: Record<string, unknown>, keys: readonly string[]): string | undefined {
  const notificationObject = payload['notification'] as Record<string, unknown> | undefined;
  const apsObject = (payload['aps'] ?? notificationObject?.['aps']) as Record<string, unknown> | undefined;
  const candidates: (Record<string, unknown> | undefined)[] = [payload['data'] as Record<string, unknown> | undefined, notificationObject?.['data'] as Record<string, unknown> | undefined, (payload['userInfo'] ?? notificationObject?.['userInfo']) as Record<string, unknown> | undefined, apsObject, notificationObject, payload];
  for (const key of keys) {
    for (const object_ of candidates) {
      if (!object_ || typeof object_ !== 'object') continue;
      const value: unknown = object_[key];
      if (typeof value === 'string' && value.trim() !== '') {
        return value.trim();
      }
    }
  }
  return undefined;
}

/**
 * Reads the `notificationType` the server put into the FCM `data` payload.
 *
 * Only relevant while the app is in the foreground: Firebase hands the message to
 * the shell instead of rendering it, so the channel the server named on the message
 * is never used and the WebView has to pass the type back down through
 * `showNotification`. iOS and Android nest the payload differently, hence the
 * candidate list - it deliberately omits `aps`, which `sendFcmNotification` never
 * writes this key into; move the key there and this stops finding it.
 */
export function extractNotificationType(payload: Record<string, unknown>): NotificationType {
  const notificationObject = payload['notification'] as Record<string, unknown> | undefined;
  const candidates: (Record<string, unknown> | undefined)[] = [payload['data'] as Record<string, unknown> | undefined, notificationObject?.['data'] as Record<string, unknown> | undefined, (payload['userInfo'] ?? notificationObject?.['userInfo']) as Record<string, unknown> | undefined, notificationObject, payload];
  for (const object_ of candidates) {
    if (!object_ || typeof object_ !== 'object') continue;
    if (object_['notificationType'] === 'emergency') return 'emergency';
  }
  return 'default';
}

/**
 * Records a tap on a push in PostHog, so what people do after opening one shows up in
 * their session. The push log row records the same tap, but it cannot show what happened
 * next.
 */
function capturePushOpened(properties: {
  channel: 'native';
  notification_type: NotificationType;
  target: 'chat' | 'other';
}): void {
  void import('posthog-js').then(({
    default: posthog
  }) => posthog.capture('push_notification_opened', properties)).catch((error: unknown) => console.error('Failed to load posthog-js', error));
}
export interface NativePushLogEntry {
  id: string;
  timestamp: string;
  message: string;
  data?: unknown;
}
export function useNativePush(): {
  isNativeApp: boolean;
  status: NativePushStatus;
  hasToken: boolean;
  isRegisteredOnBackend: boolean;
  isUnauthenticated: boolean;
  /** The user turned push off on this device. Kept across restarts, logouts and new tokens. */
  isOptedOut: boolean;
  requestPermission: () => void;
  deleteToken: () => void;
  openSettings: () => void;
  logs: NativePushLogEntry[];
  lastError: string | undefined;
} {
  const router = useRouter();
  const trpcUtils = useOptionalTrpcUtils();
  const [isNativeApp, setIsNativeApp] = useState(false);
  const [status, setStatus] = useState<NativePushStatus>('unknown');
  const [hasToken, setHasToken] = useState(false);
  const [isRegisteredOnBackend, setIsRegisteredOnBackend] = useState(false);
  const [isUnauthenticated, setIsUnauthenticated] = useState(false);
  const [logs, setLogs] = useState<NativePushLogEntry[]>([]);
  const [lastError, setLastError] = useState<string | undefined>();
  const rollbackTimeoutReference = useRef<NodeJS.Timeout | undefined>(undefined);
  const hasReceivedBridgeEventReference = useRef(false);
  /**
   * Token this device has already registered, and the one currently in flight. The
   * bridge re-emits `native-push-token` several times per permission change - three
   * times in the same millisecond is normal - and every emission used to fire its own
   * `registerDevice`. Batched together they race server-side over the same row, most
   * of them lose, and the user sees "registration failed" for a subscription that was
   * in fact stored. One registration per token is all the backend ever needed.
   */
  const registeredTokenReference = useRef<string | undefined>(undefined);
  const registrationInFlightReference = useRef<string | undefined>(undefined);
  /** Token already removed from the backend because it arrived while push is opted out. */
  const optedOutTokenReference = useRef<string | undefined>(undefined);
  const isOptedOut = usePreference('native-push-opted-out');
  const addLog = (message: string, data?: unknown): void => {
    const time = new Date().toLocaleTimeString('en-GB', {
      hour12: false
    });
    const entry: NativePushLogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      timestamp: time,
      message,
      data
    };
    setLogs(previous => [...previous.slice(-49), entry]);
  };
  const {
    mutateAsync: registerDevice
  } = trpc.nativePush.registerDevice.useMutation();
  const {
    mutateAsync: unregisterDevice
  } = trpc.nativePush.unregisterDevice.useMutation();
  const {
    mutate: markInteracted
  } = trpc.pushTracking.markInteracted.useMutation();
  const {
    mutate: markDelivered
  } = trpc.pushTracking.markDelivered.useMutation();
  useEffect(() => {
    const isNative = isNativeAppWebView();
    const isBridgeReady = nativePushBridge.isSupported();
    console.log('[NativePush:PWA] hook mounted: isNative =', isNative, '| bridgeReady =', isBridgeReady);
    const initTimeoutId = setTimeout(() => {
      setIsNativeApp(isNative && isBridgeReady);
      addLog(`Hook mounted: isNative=${String(isNative)}, bridgeReady=${String(isBridgeReady)}`);
      addLog('Requesting initial status via getStatus()');
    }, 0);
    if (!isNative) return (): void => clearTimeout(initTimeoutId);
    const handleRegisterDevice = async (token: string, platform: 'ios' | 'android'): Promise<void> => {
      // A repeat emission for a token that is already registered, or one whose call is
      // still open, carries no new information - it only adds a competitor for the same
      // row. A failed attempt clears both, so a genuine retry still gets through.
      if (registeredTokenReference.current === token) {
        console.log('[NativePush:PWA] registerDevice: token already registered, skipping');
        return;
      }
      if (registrationInFlightReference.current === token) {
        console.log('[NativePush:PWA] registerDevice: registration already in flight, skipping');
        return;
      }
      registrationInFlightReference.current = token;
      try {
        console.log('[NativePush:PWA] calling registerDevice: platform =', platform);
        addLog(`Calling registerDevice: platform=${platform}`);
        const {
          getOrCreateDeviceId
        } = await import('@/utils/device-id');
        const deviceId = getOrCreateDeviceId();
        await registerDevice({
          token,
          platform,
          deviceId
        });
        registeredTokenReference.current = token;
        console.log('[NativePush:PWA] registerDevice: success');
        addLog(`registerDevice: success`);
        setIsRegisteredOnBackend(true);
        setIsUnauthenticated(false);
        setLastError(undefined);
      } catch (error: unknown) {
        // Only this token's failure says anything about this token. A registration for
        // a token that has since been rotated away must not clear the record of the
        // newer one, or a repeat emission of it would register a second time.
        if (registeredTokenReference.current === token) {
          registeredTokenReference.current = undefined;
        }
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('[NativePush:PWA] registerDevice: failed', error);
        addLog(`registerDevice failed: ${errorMessage}`, error);
        setIsRegisteredOnBackend(false);
        const isAuthError = errorMessage.toLowerCase().includes('user not authenticated') || errorMessage.toLowerCase().includes('user not found') || errorMessage.toLowerCase().includes('forbidden') || errorMessage.toLowerCase().includes('unauthorized');
        if (isAuthError) {
          setIsUnauthenticated(true);
          setLastError(undefined);
        } else {
          setLastError(`registerDevice error: ${errorMessage}`);
        }
        if (error instanceof Error) {
          try {
            const {
              default: ph
            } = await import('posthog-js');
            ph.capture('native_push_register_error', {
              error: error.message
            });
          } catch (importError) {
            console.error('Failed to load posthog-js', importError);
          }
        }
      } finally {
        // Clear the slot only if it is still ours. Two registrations for different
        // tokens can overlap, and the first to settle would otherwise release the
        // marker the second is still relying on.
        if (registrationInFlightReference.current === token) {
          registrationInFlightReference.current = undefined;
        }
      }
    };
    const handleUnregisterDevice = async (token_0: string, platform_0: 'ios' | 'android'): Promise<void> => {
      // The token is on its way out, so let a later registration of the same token
      // through instead of treating it as already registered.
      if (registeredTokenReference.current === token_0) {
        registeredTokenReference.current = undefined;
      }
      try {
        console.log('[NativePush:PWA] calling unregisterDevice: platform =', platform_0);
        addLog(`Calling unregisterDevice: platform=${platform_0}`);
        await unregisterDevice({
          token: token_0,
          platform: platform_0
        });
        console.log('[NativePush:PWA] unregisterDevice: success');
        addLog(`unregisterDevice: success`);
      } catch (error_0: unknown) {
        const errorMessage_0 = error_0 instanceof Error ? error_0.message : String(error_0);
        console.error('[NativePush:PWA] unregisterDevice: failed', error_0);
        addLog(`unregisterDevice failed: ${errorMessage_0}`);
      }
    };

    // The same handler is attached to both `globalThis` and `document` below, because
    // the native side is not consistent about where it dispatches. An event that
    // bubbles from `document` to the window therefore arrives twice - same Event
    // object, so it can be recognised and dropped rather than handled again.
    const handledEvents = new WeakSet<Event>();
    const handleNativeEvent = (event: Event): void => {
      if (handledEvents.has(event)) return;
      handledEvents.add(event);
      hasReceivedBridgeEventReference.current = true;
      const customEvent = event as CustomEvent<NativePushEventDetail | null | undefined>;
      const detail = customEvent.detail ?? {};
      const type = detail.type;
      const payload = detail.payload ?? {};
      console.log('[NativePush:PWA] event received:', type);
      addLog(`Event received: ${type ?? 'unknown'}`, payload);
      switch (type) {
        case 'native-push-ready':
          {
            console.log('[NativePush:PWA] bridge ready, requesting status');
            setIsNativeApp(true);
            nativePushBridge.getStatus();
            break;
          }
        case 'native-push-status':
          {
            const statusValue = payload['authorizationLabel'] ?? payload['status'];
            if (typeof statusValue === 'string') {
              const tokenValue = payload['token'];
              const hasTokenValue = payload['hasToken'] === undefined ? typeof tokenValue === 'string' && tokenValue !== '' : payload['hasToken'] === true || payload['hasToken'] === 'true';
              console.log('[NativePush:PWA] status update: authLabel =', statusValue, '| hasToken =', hasTokenValue);
              let normalizedStatus: NativePushStatus = 'unknown';
              switch (statusValue) {
                case 'authorized':
                case 'granted':
                case 'provisional':
                  {
                    normalizedStatus = 'granted';
                    break;
                  }
                case 'denied':
                  {
                    normalizedStatus = 'denied';
                    break;
                  }
                case 'not-determined':
                case 'prompt':
                  {
                    normalizedStatus = 'prompt';
                    break;
                  }
              }
              setStatus(normalizedStatus);
              setHasToken(hasTokenValue);
              setLastError(undefined);
            }
            break;
          }
        case 'native-push-token':
          {
            const token_2 = payload['token'];
            const platform_2 = payload['platform'];
            console.log('[NativePush:PWA] token received: platform =', platform_2);
            if (typeof token_2 === 'string' && typeof platform_2 === 'string') {
              // The native shell has Firebase issue a new token right after deleting one and
              // reports it on every resume, so a token turning up is no sign the user wants
              // push. While they opted out it is not registered, and removed from the backend
              // once in case an earlier build or a failed delete left it there.
              if (!readPreference('native-push-opted-out')) {
                optedOutTokenReference.current = undefined;
                void handleRegisterDevice(token_2, platform_2 as 'ios' | 'android');
              } else if (optedOutTokenReference.current !== token_2) {
                optedOutTokenReference.current = token_2;
                void handleUnregisterDevice(token_2, platform_2 as 'ios' | 'android');
              }
              setStatus('granted');
              setHasToken(true);
              setLastError(undefined);
            }
            break;
          }
        case 'native-push-token-deleted':
          {
            if (rollbackTimeoutReference.current) {
              clearTimeout(rollbackTimeoutReference.current);
              rollbackTimeoutReference.current = undefined;
            }
            const token_1 = payload['token'];
            const platform_1 = payload['platform'];
            console.log('[NativePush:PWA] token deleted: platform =', platform_1);
            if (typeof token_1 === 'string' && typeof platform_1 === 'string') {
              void handleUnregisterDevice(token_1, platform_1 as 'ios' | 'android');
            }
            setHasToken(false);
            setStatus('prompt');
            setLastError(undefined);
            break;
          }
        case 'native-push-open':
          {
            const targetPath_0 = resolvePushTarget(extractTargetUrl(payload));
            let targetChatId_0: string | undefined;
            if (targetPath_0.includes('/app/chat/')) {
              const parts_0 = targetPath_0.split('/app/chat/');
              const firstPart_0 = parts_0[1];
              if (typeof firstPart_0 === 'string' && firstPart_0 !== '') {
                targetChatId_0 = firstPart_0.split(/[?#]/)[0];
              }
            }
            refreshAndOptimisticallyUpdateChat(trpcUtils, targetChatId_0, payload);

            // Native pushes never reach the service worker, whose click handler tracks
            // taps on web pushes, so the tap is recorded here.
            const notificationLogId = extractNotificationLogId(payload);
            if (notificationLogId !== undefined) {
              markInteracted({
                id: notificationLogId,
                type: 'CLICK'
              });
            }
            capturePushOpened({
              channel: 'native',
              notification_type: extractNotificationType(payload),
              target: targetChatId_0 === undefined ? 'other' : 'chat'
            });
            console.log('[NativePush:PWA] notification opened, navigating to:', targetPath_0);
            performReliablePushNavigation(router, targetPath_0);
            break;
          }
        case 'native-push-received':
        case 'native-push-message':
        case 'push-received':
        case 'notification':
          {
            const rawUrl = extractTargetUrl(payload);
            let targetChatId: string | undefined;
            if (typeof rawUrl === 'string' && rawUrl.includes('/app/chat/')) {
              const parts = rawUrl.split('/app/chat/');
              const firstPart = parts[1];
              if (typeof firstPart === 'string' && firstPart !== '') {
                targetChatId = firstPart.split(/[?#]/)[0];
              }
            }
            console.log(`[Chat][FCM] Foreground push message received (chat: ${targetChatId ?? 'unknown'}).`);

            // The only receipt a native push can report: in the background the OS renders it
            // without running any app code.
            const receivedLogId = extractNotificationLogId(payload);
            if (receivedLogId !== undefined) {
              markDelivered({
                id: receivedLogId
              });
            }

            // Refresh query cache in background
            refreshAndOptimisticallyUpdateChat(trpcUtils, targetChatId, payload);
            const {
              title: notificationTitle,
              body: notificationBody
            } = extractNotificationTitleAndBody(payload);
            let targetPath = resolvePushTarget(rawUrl);
            if (targetPath === '/app/dashboard' && typeof targetChatId === 'string' && targetChatId.trim() !== '') {
              targetPath = `/app/chat/${targetChatId.trim()}`;
            }

            // Firebase never renders a notification while the app is in the
            // foreground, so the WebView has to surface it. `notifyForegroundMessage`
            // owns the "is the user already looking at this chat?" decision and
            // de-duplicates against the same message arriving over SSE.
            notifyForegroundMessage({
              chatId: targetChatId,
              messageId: extractMessageIdentifier(payload),
              title: notificationTitle,
              body: notificationBody,
              targetPath,
              notificationType: extractNotificationType(payload)
            });
            break;
          }
        case 'native-push-error':
          {
            const rawError = payload['error'];
            const errorDetail = typeof rawError === 'string' ? rawError : JSON.stringify(rawError ?? 'Unknown native bridge error');
            console.error('[NativePush:PWA] bridge error:', payload['error']);
            addLog(`Bridge error: ${errorDetail}`);
            setLastError(errorDetail);
            setStatus(previous_0 => previous_0 === 'unknown' ? 'prompt' : previous_0);
            if (rollbackTimeoutReference.current) {
              clearTimeout(rollbackTimeoutReference.current);
              rollbackTimeoutReference.current = undefined;
            }
            break;
          }
      }
    };
    const handleAppResume = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        checkAndExecutePendingPushNavigation(router);
        if (nativePushBridge.isSupported()) {
          console.log('[NativePush:PWA] app resumed, requesting updated status');
          nativePushBridge.getStatus();
        }
      }
    };
    checkAndExecutePendingPushNavigation(router);
    globalThis.addEventListener('app-webview-native-push-event', handleNativeEvent);
    if (typeof document !== 'undefined') {
      document.addEventListener('app-webview-native-push-event', handleNativeEvent);
    }
    globalThis.addEventListener('visibilitychange', handleAppResume);
    globalThis.addEventListener('focus', handleAppResume);
    globalThis.addEventListener('pageshow', handleAppResume);
    nativePushBridge.getStatus();
    const statusTimeoutId = setTimeout(() => {
      setStatus(previous_1 => {
        if (previous_1 === 'unknown') {
          console.log('[NativePush:PWA] status timeout: assuming prompt');
          return 'prompt';
        }
        return previous_1;
      });
    }, 4000);

    // Older app builds stop injecting bridge events once a client-side
    // navigation marks the WebView "not ready" (fixed natively in
    // konekta-app#35): commands still reach the native side, but every
    // response is queued indefinitely. Only a real page load flushes that
    // queue, so if the bridge object exists but no event arrives, reload once.
    const bridgeRecoveryTimeoutId = setTimeout(() => {
      if (hasReceivedBridgeEventReference.current || !nativePushBridge.isSupported()) return;
      let lastAttempt = 0;
      try {
        const rawLastAttempt = sessionStorage.getItem(BRIDGE_RECOVERY_ATTEMPT_STORAGE_KEY);
        lastAttempt = rawLastAttempt === null ? 0 : Number(rawLastAttempt);
        if (Number.isNaN(lastAttempt)) lastAttempt = 0;
      } catch {
        // Without storage we cannot rate-limit the reload, so skip recovery.
        return;
      }
      if (Date.now() - lastAttempt < BRIDGE_RECOVERY_RETRY_INTERVAL_MS) {
        addLog('Bridge unresponsive, but recovery reload was already attempted recently');
        return;
      }
      try {
        sessionStorage.setItem(BRIDGE_RECOVERY_ATTEMPT_STORAGE_KEY, String(Date.now()));
      } catch {
        return;
      }
      console.warn('[NativePush:PWA] no bridge event received, reloading to restore bridge');
      addLog('No bridge event received, reloading page to restore native bridge');
      reloadPage();
    }, BRIDGE_RECOVERY_TIMEOUT_MS);
    return (): void => {
      clearTimeout(initTimeoutId);
      clearTimeout(statusTimeoutId);
      clearTimeout(bridgeRecoveryTimeoutId);
      if (rollbackTimeoutReference.current) {
        clearTimeout(rollbackTimeoutReference.current);
      }
      globalThis.removeEventListener('app-webview-native-push-event', handleNativeEvent);
      if (typeof document !== 'undefined') {
        document.removeEventListener('app-webview-native-push-event', handleNativeEvent);
      }
      globalThis.removeEventListener('visibilitychange', handleAppResume);
      globalThis.removeEventListener('focus', handleAppResume);
      globalThis.removeEventListener('pageshow', handleAppResume);
    };
  }, [router, registerDevice, unregisterDevice, markInteracted, markDelivered, trpcUtils]);
  const requestPermission = (): void => {
    Cookies.remove(Cookie.SKIP_PUSH_NOTIFICATION);
    writePreference('native-push-opted-out', false);
    addLog('requestPermission() called');
    setLastError(undefined);
    if (isNativeApp || nativePushBridge.isSupported()) {
      console.log('[NativePush:PWA] requestPermission called');
      nativePushBridge.requestPermission();
      setTimeout(() => nativePushBridge.getStatus(), 500);
      setTimeout(() => nativePushBridge.getStatus(), 1500);
      setTimeout(() => nativePushBridge.getStatus(), 3000);
    }
    if (typeof globalThis !== 'undefined' && 'Notification' in globalThis && typeof globalThis.Notification.requestPermission === 'function') {
      addLog('Triggering Notification.requestPermission() fallback');
      void globalThis.Notification.requestPermission().then(permission => {
        addLog(`Notification.requestPermission() result: ${permission}`);
        if (permission === 'granted') {
          setStatus('granted');
          if (nativePushBridge.isSupported()) {
            nativePushBridge.getStatus();
          }
        } else if (permission === 'denied') {
          setStatus('denied');
        }
      }).catch((error_1: unknown) => {
        addLog(`Notification.requestPermission error: ${String(error_1)}`);
      });
    }
  };
  const deleteToken = (): void => {
    addLog('deleteToken() called');
    writePreference('native-push-opted-out', true);
    setIsRegisteredOnBackend(false);
    setLastError(undefined);
    if (isNativeApp) {
      console.log('[NativePush:PWA] deleteToken called');
      if (rollbackTimeoutReference.current) {
        clearTimeout(rollbackTimeoutReference.current);
      }
      const previousHasToken = hasToken;
      const previousStatus = status;
      setHasToken(false);
      setStatus('prompt');
      rollbackTimeoutReference.current = setTimeout(() => {
        console.warn('[NativePush:PWA] deleteToken timeout: rolling back optimistic update');
        addLog('deleteToken timeout: rolling back optimistic update');
        setHasToken(previousHasToken);
        setStatus(previousStatus);
      }, 5000);
      nativePushBridge.deleteToken();
    }
  };
  const openSettings = (): void => {
    Cookies.remove(Cookie.SKIP_PUSH_NOTIFICATION);
    writePreference('native-push-opted-out', false);
    addLog('openSettings() called');
    setLastError(undefined);
    if (isNativeApp) {
      console.log('[NativePush:PWA] openSettings called');
      nativePushBridge.openSettings();
    }
  };
  return {
    isNativeApp,
    status,
    hasToken,
    isRegisteredOnBackend,
    isUnauthenticated,
    isOptedOut,
    requestPermission,
    deleteToken,
    openSettings,
    logs,
    lastError
  };
}